import { Card, Table, Tag, Title, Text, Spinner, Icon } from '@nimbus-ds/components';
import {
  ArrowupIcon,
  ArrowDownIcon,
  ArrowsHorizontalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  QuestionCircleIcon,
} from '@nimbus-ds/icons';
import type { CustomEventMetrics, CustomEventsPropensity } from '@/api/customEvents';

interface CustomEventsTableProps {
  events: CustomEventMetrics[];
  summary?: {
    total_events: number;
    unique_visitors_with_events: number;
    total_event_definitions: number;
  };
  propensity?: CustomEventsPropensity;
  isLoading?: boolean;
}

type TagAppearance = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

const getCategoryAppearance = (category: string): TagAppearance => {
  const appearances: Record<string, TagAppearance> = {
    interaction: 'primary',
    conversion_signal: 'success',
    engagement: 'neutral',
    navigation: 'neutral',
  };
  return appearances[category] || 'neutral';
};

const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

const formatLift = (lift: number): string => {
  const sign = lift >= 0 ? '+' : '';
  return `${sign}${(lift * 100).toFixed(0)}%`;
};

const getLiftColor = (lift: number, isSignificant: boolean): string => {
  if (!isSignificant) return 'text-gray-500';
  if (lift > 0) return 'text-green-600';
  if (lift < 0) return 'text-red-600';
  return 'text-gray-500';
};

const getLiftIcon = (direction: string, isSignificant: boolean) => {
  if (!isSignificant) return <span className="text-gray-400"><Icon source={<ArrowsHorizontalIcon />} color="currentColor" /></span>;
  if (direction === 'positive') return <span className="text-green-600"><Icon source={<ArrowupIcon />} color="currentColor" /></span>;
  if (direction === 'negative') return <span className="text-red-600"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>;
  return <span className="text-gray-400"><Icon source={<ArrowsHorizontalIcon />} color="currentColor" /></span>;
};

export function CustomEventsTable({ events, summary, propensity, isLoading }: CustomEventsTableProps) {
  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Eventos Personalizados</Title>
          <Text>Cargando métricas de eventos...</Text>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center justify-center py-8">
            <Spinner size="large" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Eventos Personalizados</Title>
          <Text>Interacciones y comportamientos personalizados</Text>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-8">
            <Text>No hay eventos personalizados configurados.</Text>
            <Text fontSize="caption">Crea eventos para trackear interacciones específicas de tus usuarios.</Text>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between">
          <div>
            <Title as="h3">Eventos Personalizados</Title>
            <Text>
              {summary ? (
                <>
                  {formatNumber(summary.total_event_definitions)} evento{summary.total_event_definitions !== 1 ? 's' : ''} · {' '}
                  {formatNumber(summary.total_events)} ocurrencias · {' '}
                  {formatNumber(summary.unique_visitors_with_events)} sesiones únicas
                </>
              ) : (
                'Interacciones y comportamientos personalizados'
              )}
            </Text>
          </div>
          {propensity && (
            <div
              className="flex items-center gap-1 cursor-help"
              title="Compara la tasa de conversión de sesiones que dispararon un evento vs las que no. Un lift positivo indica mayor probabilidad de compra."
            >
              <Icon source={<QuestionCircleIcon />} />
              <Text fontSize="caption" as="span">Análisis de propensión</Text>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <Table minWidth="760px" stickyScrollbar>
          <Table.Head>
            <Table.Row>
              <Table.Cell as="th">Evento</Table.Cell>
              <Table.Cell as="th">Categoría</Table.Cell>
              <Table.Cell as="th" className="text-right">Sesiones</Table.Cell>
              <Table.Cell as="th" className="text-right">
                <span className="cursor-help underline decoration-dotted" title="Porcentaje de sesiones totales que dispararon este evento">
                  Tasa Sesión
                </span>
              </Table.Cell>
              <Table.Cell as="th" className="text-right">Ocurrencias</Table.Cell>
              {propensity && (
                <>
                  <Table.Cell as="th" className="text-right">
                    <span className="cursor-help underline decoration-dotted" title="Tasa de conversión de sesiones que dispararon este evento">
                      Tasa Conv.
                    </span>
                  </Table.Cell>
                  <Table.Cell as="th" className="text-right">
                    <span className="cursor-help underline decoration-dotted" title="Tasa de conversión general de la tienda">
                      Baseline
                    </span>
                  </Table.Cell>
                  <Table.Cell as="th" className="text-right">
                    <span className="cursor-help underline decoration-dotted" title="Cambio relativo vs baseline. +50% = 1.5x más probable de convertir">
                      Lift
                    </span>
                  </Table.Cell>
                  <Table.Cell as="th" className="text-center">
                    <span className="cursor-help underline decoration-dotted" title="Significancia estadística (p < 0.05)">
                      Sig.
                    </span>
                  </Table.Cell>
                </>
              )}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {events.map((event) => {
              const eventPropensity = propensity?.events?.[event.event_id];

              return (
                <Table.Row key={event.event_id}>
                  <Table.Cell className="font-medium">
                    <div>
                      <div className="font-medium">{event.display_name}</div>
                      <Text fontSize="caption" as="span">{event.event_name}</Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Tag appearance={getCategoryAppearance(event.category)}>
                      {event.category.replace('_', ' ')}
                    </Tag>
                  </Table.Cell>
                  <Table.Cell className="text-right font-mono">
                    {formatNumber(event.unique_sessions)}
                  </Table.Cell>
                  <Table.Cell className="text-right font-mono">
                    {eventPropensity?.session_engagement_rate != null
                      ? formatPercentage(eventPropensity.session_engagement_rate)
                      : event.session_engagement_rate != null
                        ? formatPercentage(event.session_engagement_rate)
                        : '-'
                    }
                  </Table.Cell>
                  <Table.Cell className="text-right font-mono">
                    {formatNumber(event.total_occurrences)}
                  </Table.Cell>
                  {propensity && eventPropensity && (
                    <>
                      <Table.Cell className="text-right font-mono">
                        {formatPercentage(eventPropensity.conversion_rate_with_event)}
                      </Table.Cell>
                      <Table.Cell className="text-right font-mono text-gray-500">
                        {formatPercentage(eventPropensity.baseline_conversion_rate)}
                      </Table.Cell>
                      <Table.Cell className={`text-right font-mono font-semibold ${getLiftColor(eventPropensity.conversion_lift, eventPropensity.is_significant)}`}>
                        <div className="flex items-center justify-end gap-1">
                          {getLiftIcon(eventPropensity.direction, eventPropensity.is_significant)}
                          {formatLift(eventPropensity.conversion_lift)}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <span
                          title={
                            !eventPropensity.sample_size_adequate
                              ? `Low sample size (${eventPropensity.sessions_with_event} sessions)`
                              : eventPropensity.is_significant
                              ? `Statistically significant (p=${eventPropensity.p_value.toFixed(3)})`
                              : `Not significant (p=${eventPropensity.p_value.toFixed(3)})`
                          }
                          className="cursor-help"
                        >
                          {!eventPropensity.sample_size_adequate ? (
                            <span className="text-yellow-500 mx-auto"><Icon source={<ExclamationTriangleIcon />} color="currentColor" /></span>
                          ) : eventPropensity.is_significant ? (
                            <span className="text-green-600 mx-auto"><Icon source={<CheckCircleIcon />} color="currentColor" /></span>
                          ) : (
                            <span className="text-gray-400 mx-auto"><Icon source={<ArrowsHorizontalIcon />} color="currentColor" /></span>
                          )}
                        </span>
                      </Table.Cell>
                    </>
                  )}
                  {propensity && !eventPropensity && (
                    <>
                      <Table.Cell className="text-right text-gray-400">-</Table.Cell>
                      <Table.Cell className="text-right text-gray-400">-</Table.Cell>
                      <Table.Cell className="text-right text-gray-400">-</Table.Cell>
                      <Table.Cell className="text-center text-gray-400">-</Table.Cell>
                    </>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        {events.length > 0 && propensity && (
          <div className="mt-4 pt-4 border-t">
            <Text fontSize="caption">
              Sesiones que dispararon el evento tuvieron una tasa de conversión de <strong>Tasa Conv.</strong> vs <strong>{formatPercentage(propensity.baseline_conversion_rate)}</strong> baseline.
              Un <strong>Lift</strong> positivo indica mayor propensión a convertir.
            </Text>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default CustomEventsTable;
